/**
 * UnifiedAudioService
 * 
 * Cross-browser audio service using standardized-audio-context for consistent
 * Web Audio API behavior across browsers (Safari, iOS, Chrome, Firefox).
 * 
 * Key features:
 * - Single shared AudioContext for both music and ATC
 * - Gain-based volume control (works on iOS where HTMLMediaElement.volume doesn't)
 * - Proper mute support via gain nodes
 * - iOS detection and Web Audio routing recommendations
 * - Prevents double audio output issues
 * 
 * Architecture:
 * - Music uses Howler.js with html5:false on iOS (routes through Web Audio)
 * - ATC uses Web Audio gain nodes, analyzer fed post-gain
 * - Both share the same AudioContext for potential future cross-audio features
 */

import { AudioContext, GainNode } from 'standardized-audio-context';
import { Logger } from '@/app/utils/logger';

const LOG_TAG = 'unified-audio';

const isRecoverableAudioContextState = (state: string | undefined | null): boolean => (
  state === 'suspended' || state === 'interrupted'
);

const isSuspendableAudioContextState = (state: string | undefined | null): boolean => (
  state === 'running'
);

export interface AudioChannelConfig {
  initialVolume?: number; // 0-100
  isMuted?: boolean;
}

export interface AudioChannel {
  gainNode: GainNode<AudioContext>;
  volume: number; // 0-100
  isMuted: boolean;
  previousVolume: number; // For unmute restoration
}

class UnifiedAudioService {
  private static instance: UnifiedAudioService;

  private audioContext: AudioContext | null = null;
  private isUnlocked = false;

  // Audio channels
  private musicChannel: AudioChannel | null = null;
  private atcChannel: AudioChannel | null = null;
  private masterChannel: AudioChannel | null = null;

  // Track media elements that have already been bound to a MediaElementSourceNode.
  // A given HTMLMediaElement can only ever be used to create one source node, even
  // after that node is disconnected, so we keep a weak association for element reuse.
  private connectedElements: WeakMap<HTMLMediaElement, MediaElementAudioSourceNode> = new WeakMap();
  private trackedConnectedElements: Set<HTMLMediaElement> = new Set();

  private constructor() {
    // Private constructor for singleton
  }

  static getInstance(): UnifiedAudioService {
    if (!UnifiedAudioService.instance) {
      UnifiedAudioService.instance = new UnifiedAudioService();
    }
    return UnifiedAudioService.instance;
  }

  /**
   * Get or create the shared AudioContext
   */
  getAudioContext(): AudioContext {
    if (!this.audioContext) {
      Logger.info('Creating new standardized AudioContext', LOG_TAG);
      this.audioContext = new AudioContext();
      this.setupUnlockHandler();
    }
    return this.audioContext;
  }

  /**
   * Setup handlers to unlock AudioContext on user interaction (required by browsers)
   */
  private setupUnlockHandler(): void {
    if (this.isUnlocked || !this.audioContext) return;

    const unlock = async () => {
      if (!this.audioContext || this.isUnlocked) return;

      const previousState = this.audioContext.state;

      if (isRecoverableAudioContextState(previousState)) {
        try {
          await this.audioContext.resume();
          Logger.info('AudioContext unlocked via user interaction', LOG_TAG, {
            previousState,
          });
        } catch (error) {
          Logger.warn('Failed to unlock AudioContext', LOG_TAG, { error });
        }
      }

      this.isUnlocked = true;

      // Remove listeners after unlock
      document.removeEventListener('touchstart', unlock, true);
      document.removeEventListener('touchend', unlock, true);
      document.removeEventListener('click', unlock, true);
      document.removeEventListener('keydown', unlock, true);
    };

    // Add unlock listeners for various user interactions
    document.addEventListener('touchstart', unlock, { once: true, capture: true });
    document.addEventListener('touchend', unlock, { once: true, capture: true });
    document.addEventListener('click', unlock, { once: true, capture: true });
    document.addEventListener('keydown', unlock, { once: true, capture: true });
  }

  /**
   * Resume AudioContext if suspended (call this on user interaction)
   */
  async resume(): Promise<void> {
    const previousState = this.audioContext?.state;

    if (this.audioContext && isRecoverableAudioContextState(previousState)) {
      try {
        await this.audioContext.resume();
        Logger.debug('AudioContext resumed', LOG_TAG, { previousState });
      } catch (error) {
        Logger.error('Failed to resume AudioContext', LOG_TAG, { error });
      }
    }
  }

  /**
   * Suspend AudioContext when synchronized playback becomes idle.
   * Safari is more reliable when the shared graph is explicitly torn down
   * instead of being left running while all sources are paused.
   */
  async suspend(): Promise<void> {
    const previousState = this.audioContext?.state;

    if (this.audioContext && isSuspendableAudioContextState(previousState)) {
      try {
        await this.audioContext.suspend();
        Logger.debug('AudioContext suspended', LOG_TAG, { previousState });
      } catch (error) {
        Logger.error('Failed to suspend AudioContext', LOG_TAG, { error });
      }
    }
  }

  /**
   * Check if AudioContext is ready for use
   */
  isReady(): boolean {
    return this.audioContext !== null && this.audioContext.state === 'running';
  }

  /**
   * Create an audio channel with its own gain node
   */
  private createChannel(config: AudioChannelConfig = {}): AudioChannel {
    const ctx = this.getAudioContext();
    const gainNode = ctx.createGain();

    const volume = config.initialVolume ?? 100;
    const isMuted = config.isMuted ?? false;

    gainNode.gain.value = isMuted ? 0 : volume / 100;

    return {
      gainNode,
      volume,
      isMuted,
      previousVolume: volume,
    };
  }

  /**
   * Get the music channel gain node
   */
  getMusicChannel(config?: AudioChannelConfig): AudioChannel {
    if (!this.musicChannel) {
      Logger.info('Creating music audio channel', LOG_TAG);
      this.musicChannel = this.createChannel(config);

      // Connect to master if exists, otherwise to destination
      const destination = this.masterChannel?.gainNode ?? this.getAudioContext().destination;
      this.musicChannel.gainNode.connect(destination);
    }
    return this.musicChannel;
  }

  /**
   * Get the ATC channel gain node
   */
  getAtcChannel(config?: AudioChannelConfig): AudioChannel {
    if (!this.atcChannel) {
      Logger.info('Creating ATC audio channel', LOG_TAG);
      this.atcChannel = this.createChannel(config);

      // Connect to master if exists, otherwise to destination
      const destination = this.masterChannel?.gainNode ?? this.getAudioContext().destination;
      this.atcChannel.gainNode.connect(destination);
    }
    return this.atcChannel;
  }

  /**
   * Get the master channel gain node (optional, for global volume/mute)
   */
  getMasterChannel(config?: AudioChannelConfig): AudioChannel {
    if (!this.masterChannel) {
      Logger.info('Creating master audio channel', LOG_TAG);
      this.masterChannel = this.createChannel(config);
      this.masterChannel.gainNode.connect(this.getAudioContext().destination);

      // Reconnect existing channels through master
      if (this.musicChannel) {
        this.musicChannel.gainNode.disconnect();
        this.musicChannel.gainNode.connect(this.masterChannel.gainNode);
      }
      if (this.atcChannel) {
        this.atcChannel.gainNode.disconnect();
        this.atcChannel.gainNode.connect(this.masterChannel.gainNode);
      }
    }
    return this.masterChannel;
  }

  /**
   * Set volume for a channel (0-100)
   */
  setChannelVolume(channel: AudioChannel, volume: number): void {
    const clampedVolume = Math.max(0, Math.min(100, volume));
    channel.volume = clampedVolume;

    if (!channel.isMuted) {
      channel.gainNode.gain.value = clampedVolume / 100;
    }

    Logger.debug(`Channel volume set to ${clampedVolume}`, LOG_TAG);
  }

  /**
   * Set mute state for a channel
   */
  setChannelMuted(channel: AudioChannel, muted: boolean): void {
    channel.isMuted = muted;

    if (muted) {
      channel.previousVolume = channel.volume;
      channel.gainNode.gain.value = 0;
    } else {
      channel.gainNode.gain.value = channel.volume / 100;
    }

    Logger.debug(`Channel ${muted ? 'muted' : 'unmuted'}`, LOG_TAG);
  }

  /**
   * Toggle mute state for a channel
   */
  toggleChannelMute(channel: AudioChannel): boolean {
    this.setChannelMuted(channel, !channel.isMuted);
    return channel.isMuted;
  }

  // ================== Music Volume Helpers ==================

  setMusicVolume(volume: number): void {
    if (this.musicChannel) {
      this.setChannelVolume(this.musicChannel, volume);
    }
  }

  setMusicMuted(muted: boolean): void {
    if (this.musicChannel) {
      this.setChannelMuted(this.musicChannel, muted);
    }
  }

  getMusicVolume(): number {
    return this.musicChannel?.volume ?? 100;
  }

  isMusicMuted(): boolean {
    return this.musicChannel?.isMuted ?? false;
  }

  // ================== ATC Volume Helpers ==================

  setAtcVolume(volume: number): void {
    if (this.atcChannel) {
      this.setChannelVolume(this.atcChannel, volume);
    }
  }

  setAtcMuted(muted: boolean): void {
    if (this.atcChannel) {
      this.setChannelMuted(this.atcChannel, muted);
    }
  }

  getAtcVolume(): number {
    return this.atcChannel?.volume ?? 100;
  }

  isAtcMuted(): boolean {
    return this.atcChannel?.isMuted ?? false;
  }

  // ================== Master Volume Helpers ==================

  setMasterVolume(volume: number): void {
    const master = this.getMasterChannel();
    this.setChannelVolume(master, volume);
  }

  setMasterMuted(muted: boolean): void {
    const master = this.getMasterChannel();
    this.setChannelMuted(master, muted);
  }

  // ================== Media Element Source Management ==================

  /**
   * Create a MediaElementSourceNode for an audio element
   * Handles the case where an element is already connected (React StrictMode)
   */
  createMediaElementSource(audioElement: HTMLMediaElement): MediaElementAudioSourceNode {
    // Check if already connected
    if (this.connectedElements.has(audioElement)) {
      this.trackedConnectedElements.add(audioElement);
      return this.connectedElements.get(audioElement)!;
    }

    try {
      // Use native AudioContext's createMediaElementSource
      // standardized-audio-context doesn't support this method directly,
      // so we need to access the underlying native context
      const nativeCtx = this.getNativeAudioContext();
      const sourceNode = nativeCtx.createMediaElementSource(audioElement);

      this.connectedElements.set(audioElement, sourceNode);
      this.trackedConnectedElements.add(audioElement);
      Logger.debug('Created MediaElementSource for audio element', LOG_TAG);

      return sourceNode;
    } catch (error) {
      Logger.error('Failed to create MediaElementSource', LOG_TAG, { error });
      throw error;
    }
  }

  /**
   * Disconnect a media element source
   */
  disconnectMediaElement(audioElement: HTMLMediaElement): void {
    if (this.connectedElements.has(audioElement)) {
      try {
        const sourceNode = this.connectedElements.get(audioElement)!;
        sourceNode.disconnect();
      } catch (error) {
        Logger.warn('Error disconnecting media element', LOG_TAG, { error });
      }
      this.trackedConnectedElements.delete(audioElement);
    }
  }

  /**
   * Get the native AudioContext (needed for libraries that don't support standardized-audio-context)
   */
  getNativeAudioContext(): globalThis.AudioContext {
    const ctx = this.getAudioContext();
    // Access the underlying native context if available
    const nativeCtx = (ctx as unknown as { _nativeContext?: globalThis.AudioContext })._nativeContext;
    return nativeCtx || (ctx as unknown as globalThis.AudioContext);
  }

  /**
   * Create an AnalyserNode for frequency visualization.
   * Uses the native AudioContext (required for AnalyserNode).
   * 
   * The AnalyserNode should be connected as a side-chain tap (parallel to destination),
   * not as a pass-through node in the audio graph.
   * 
   * @param config - Optional configuration for the AnalyserNode
   * @param config.fftSize - FFT size (must be power of 2, range: 32-32768, default: 2048)
   * @param config.smoothingTimeConstant - Temporal smoothing (0-1, default: 0.8)
   * @param config.minDecibels - Minimum dB value for scaling (default: -90)
   * @param config.maxDecibels - Maximum dB value for scaling (default: -10)
   * @returns Native AnalyserNode configured with the specified settings
   * 
   * @example
   * ```typescript
   * const analyser = unifiedAudioService.createAnalyserNode({
   *   fftSize: 1024,
   *   smoothingTimeConstant: 0.8,
   *   minDecibels: -100,
   *   maxDecibels: -30
   * });
   * 
   * // Connect as side-chain tap
   * outputNode.connect(analyser); // For visualization data
   * outputNode.connect(audioCtx.destination); // For audio output
   * 
   * // On cleanup:
   * analyser.disconnect();
   * ```
   */
  createAnalyserNode(config?: {
    fftSize?: number;
    smoothingTimeConstant?: number;
    minDecibels?: number;
    maxDecibels?: number;
  }): globalThis.AnalyserNode {
    const nativeCtx = this.getNativeAudioContext();
    const analyser = nativeCtx.createAnalyser();

    if (config?.fftSize) analyser.fftSize = config.fftSize;
    if (config?.smoothingTimeConstant !== undefined) analyser.smoothingTimeConstant = config.smoothingTimeConstant;
    if (config?.minDecibels !== undefined) analyser.minDecibels = config.minDecibels;
    if (config?.maxDecibels !== undefined) analyser.maxDecibels = config.maxDecibels;

    Logger.debug('Created AnalyserNode', LOG_TAG, { fftSize: analyser.fftSize });
    return analyser;
  }

  /**
   * Reset all channels and context (useful for testing or full cleanup)
   */
  reset(): void {
    Logger.info('Resetting UnifiedAudioService', LOG_TAG);

    // Disconnect all media elements
    this.trackedConnectedElements.forEach((element) => {
      this.disconnectMediaElement(element);
    });
    this.trackedConnectedElements.clear();

    // Disconnect channels
    try {
      this.musicChannel?.gainNode.disconnect();
      this.atcChannel?.gainNode.disconnect();
      this.masterChannel?.gainNode.disconnect();
    } catch {
      // Ignore disconnect errors
    }

    // Close context
    if (this.audioContext) {
      try {
        this.audioContext.close();
      } catch {
        // Ignore close errors
      }
    }

    // Reset state
    this.audioContext = null;
    this.musicChannel = null;
    this.atcChannel = null;
    this.masterChannel = null;
    this.connectedElements = new WeakMap();
    this.isUnlocked = false;
  }
}

// Export singleton instance
export const unifiedAudioService = UnifiedAudioService.getInstance();

// Export types
export type { AudioContext, GainNode } from 'standardized-audio-context';
